import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  adminCheckSession,
  createProgram,
  deleteProgram,
  getCategories,
  getProgram,
  previewProgram,
  publishProgram,
  setAdminToken,
  updateProgram,
} from './adminApi';
import './admin.css';

function randomSuffix(len = 6) {
  return Math.random().toString(36).slice(2, 2 + len);
}

function makeVenueId(name) {
  const base = String(name || 'venue')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 24) || 'venue';
  return `${base}-${randomSuffix(4)}`;
}

function makeOccurrenceId() {
  return `occ-${randomSuffix(8)}`;
}

function emptyVenue(id) {
  return {
    id: id || makeVenueId('venue'),
    name: '',
    street: '',
    city: '',
    state: 'CA',
    zip: '',
    country: 'US',
  };
}

function emptyOccurrence(venueId) {
  return {
    id: makeOccurrenceId(),
    date: '',
    start_time: '',
    end_time: '',
    venue_id: venueId || '',
    ticket_url: '',
    price: '',
    public_notes: '',
    internal_notes: '',
  };
}

function emptyProgram() {
  const venue = emptyVenue('venue-1');
  return {
    title: '',
    categories: [],
    description: '',
    event_url: '',
    use_event_url_as_ticket_url: false,
    price: '',
    organizer_name: '',
    organizer_email: '',
    organizer_phone: '',
    audience_suitability: '',
    image: {
      url: '',
      alt_text: '',
      credit: '',
      usage_permission_confirmed: false,
    },
    notes: '',
    venues: [venue],
    occurrences: [emptyOccurrence(venue.id)],
  };
}

function normalizeTime(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const match = raw.match(/^(\d{2}:\d{2})/);
  return match ? match[1] : raw;
}

function normalizeForSave(program) {
  const imageUrl = (program.image && program.image.url) || '';
  const image =
    imageUrl.trim() ||
    (program.image && (program.image.alt_text || program.image.credit || program.image.usage_permission_confirmed))
      ? {
          url: imageUrl.trim() || null,
          alt_text: (program.image.alt_text || '').trim() || null,
          credit: (program.image.credit || '').trim() || null,
          usage_permission_confirmed: Boolean(program.image.usage_permission_confirmed),
        }
      : null;

  return {
    ...program,
    title: (program.title || '').trim(),
    description: (program.description || '').trim(),
    event_url: (program.event_url || '').trim(),
    use_event_url_as_ticket_url: Boolean(program.use_event_url_as_ticket_url),
    price: (program.price || '').trim() || null,
    organizer_name: (program.organizer_name || '').trim() || null,
    organizer_email: (program.organizer_email || '').trim() || null,
    organizer_phone: (program.organizer_phone || '').trim() || null,
    audience_suitability: (program.audience_suitability || '').trim() || null,
    notes: (program.notes || '').trim() || null,
    image,
    categories: (program.categories || []).map((c) => String(c).trim()).filter(Boolean),
    venues: (program.venues || []).map((v) => ({
      ...v,
      id: (v.id || '').trim(),
      name: (v.name || '').trim(),
      street: (v.street || '').trim(),
      city: (v.city || '').trim(),
      state: (v.state || '').trim(),
      zip: (v.zip || '').trim(),
      country: (v.country || 'US').trim() || 'US',
    })),
    occurrences: (program.occurrences || []).map((o) => ({
      ...o,
      id: (o.id || '').trim(),
      date: (o.date || '').trim(),
      start_time: normalizeTime(o.start_time),
      end_time: normalizeTime(o.end_time) || null,
      venue_id: (o.venue_id || '').trim(),
      ticket_url: (o.ticket_url || '').trim() || null,
      price: (o.price || '').trim() || null,
      public_notes: (o.public_notes || '').trim() || null,
      internal_notes: (o.internal_notes || '').trim() || null,
    })),
  };
}

function formatMessages(list) {
  if (!list || !list.length) return [];
  return list.map((item) => (typeof item === 'string' ? item : item.message || JSON.stringify(item)));
}

function AdminProgramEditor() {
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();

  const [program, setProgram] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [errors, setErrors] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [success, setSuccess] = useState('');
  const [previewOccId, setPreviewOccId] = useState('');
  const [previewEvent, setPreviewEvent] = useState(null);
  const [previewError, setPreviewError] = useState('');
  const [imageLoadError, setImageLoadError] = useState(false);

  const redirectUnauthorized = useCallback(() => {
    setAdminToken('');
    navigate('/admin/login', { replace: true });
  }, [navigate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await adminCheckSession();
        const catData = await getCategories();
        if (cancelled) return;
        setCategories(catData.categories || []);

        if (isNew) {
          const blank = emptyProgram();
          setProgram(blank);
          setPreviewOccId(blank.occurrences[0].id);
        } else {
          const data = await getProgram(id);
          if (cancelled) return;
          const loaded = {
            ...emptyProgram(),
            ...data.data,
            image: {
              url: '',
              alt_text: '',
              credit: '',
              usage_permission_confirmed: false,
              ...(data.data.image || {}),
            },
          };
          setProgram(loaded);
          setImageLoadError(false);
          setPreviewOccId((loaded.occurrences && loaded.occurrences[0] && loaded.occurrences[0].id) || '');
        }
      } catch (err) {
        if (err.status === 401) {
          redirectUnauthorized();
          return;
        }
        if (!cancelled) setErrors([err.message || 'Failed to load program']);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, isNew, redirectUnauthorized]);

  const status = program && program.status ? program.status : 'draft';

  const updateField = (field, value) => {
    setProgram((prev) => ({ ...prev, [field]: value }));
    setSuccess('');
  };

  const updateImage = (field, value) => {
    setProgram((prev) => ({
      ...prev,
      image: { ...(prev.image || {}), [field]: value },
    }));
    if (field === 'url') setImageLoadError(false);
    setSuccess('');
  };

  const toggleCategory = (cat) => {
    setProgram((prev) => {
      const current = prev.categories || [];
      const next = current.includes(cat)
        ? current.filter((c) => c !== cat)
        : [...current, cat];
      return { ...prev, categories: next };
    });
    setSuccess('');
  };

  const updateVenue = (index, field, value) => {
    setProgram((prev) => {
      const venues = [...(prev.venues || [])];
      venues[index] = { ...venues[index], [field]: value };
      return { ...prev, venues };
    });
    setSuccess('');
  };

  const addVenue = () => {
    setProgram((prev) => ({
      ...prev,
      venues: [...(prev.venues || []), emptyVenue()],
    }));
  };

  const removeVenue = (index) => {
    setProgram((prev) => {
      const venues = [...(prev.venues || [])];
      if (venues.length <= 1) return prev;
      const removed = venues[index];
      venues.splice(index, 1);
      const fallback = venues[0].id;
      const occurrences = (prev.occurrences || []).map((o) =>
        o.venue_id === removed.id ? { ...o, venue_id: fallback } : o
      );
      return { ...prev, venues, occurrences };
    });
  };

  const updateOccurrence = (index, field, value) => {
    setProgram((prev) => {
      const occurrences = [...(prev.occurrences || [])];
      occurrences[index] = { ...occurrences[index], [field]: value };
      return { ...prev, occurrences };
    });
    setSuccess('');
  };

  const addOccurrence = () => {
    setProgram((prev) => {
      const venueId = (prev.venues && prev.venues[0] && prev.venues[0].id) || '';
      const occ = emptyOccurrence(venueId);
      setPreviewOccId(occ.id);
      return { ...prev, occurrences: [...(prev.occurrences || []), occ] };
    });
  };

  const duplicateOccurrence = (index) => {
    setProgram((prev) => {
      const occurrences = [...(prev.occurrences || [])];
      const copy = {
        ...occurrences[index],
        id: makeOccurrenceId(),
      };
      occurrences.splice(index + 1, 0, copy);
      setPreviewOccId(copy.id);
      return { ...prev, occurrences };
    });
  };

  const removeOccurrence = (index) => {
    setProgram((prev) => {
      const occurrences = [...(prev.occurrences || [])];
      if (occurrences.length <= 1) return prev;
      const removed = occurrences[index];
      occurrences.splice(index, 1);
      if (previewOccId === removed.id) {
        setPreviewOccId(occurrences[0].id);
      }
      return { ...prev, occurrences };
    });
  };

  const handleSave = async () => {
    setBusy('save');
    setErrors([]);
    setWarnings([]);
    setSuccess('');
    try {
      const payload = normalizeForSave(program);
      if (isNew) {
        const outcome = await createProgram(payload);
        setWarnings(formatMessages(outcome.warnings));
        setSuccess('Draft saved.');
        navigate(`/admin/programs/${encodeURIComponent(outcome.program.id)}`, { replace: true });
      } else {
        const outcome = await updateProgram(id, payload);
        setProgram({
          ...outcome.program,
          image: {
            url: '',
            alt_text: '',
            credit: '',
            usage_permission_confirmed: false,
            ...(outcome.program.image || {}),
          },
        });
        setWarnings(formatMessages(outcome.warnings));
        setSuccess('Draft saved.');
      }
    } catch (err) {
      if (err.status === 401) {
        redirectUnauthorized();
        return;
      }
      {
        const msgs = formatMessages(err.errors);
        setErrors(msgs.length ? msgs : [err.message || 'Save failed']);
      }
      if (err.warnings) setWarnings(formatMessages(err.warnings));
    } finally {
      setBusy('');
    }
  };

  const handlePublish = async () => {
    if (isNew) {
      setErrors(['Save the draft before publishing.']);
      return;
    }
    setBusy('publish');
    setErrors([]);
    setWarnings([]);
    setSuccess('');
    try {
      const payload = normalizeForSave(program);
      await updateProgram(id, payload);
      const outcome = await publishProgram(id);
      setProgram({
        ...outcome.program,
        image: {
          url: '',
          alt_text: '',
          credit: '',
          usage_permission_confirmed: false,
          ...(outcome.program.image || {}),
        },
      });
      setWarnings(formatMessages(outcome.warnings));
      const count = outcome.stats && outcome.stats.manual != null
        ? ` (${outcome.stats.manual} manual listings in combined events)`
        : '';
      setSuccess(`Published.${count}`);
    } catch (err) {
      if (err.status === 401) {
        redirectUnauthorized();
        return;
      }
      setErrors(formatMessages(err.errors).length ? formatMessages(err.errors) : [err.message]);
      if (err.warnings) setWarnings(formatMessages(err.warnings));
    } finally {
      setBusy('');
    }
  };

  const handleDelete = async () => {
    if (isNew) {
      navigate('/admin');
      return;
    }
    if (!window.confirm(`Delete program "${program.title || id}"? This cannot be undone.`)) return;
    setBusy('delete');
    try {
      await deleteProgram(id);
      navigate('/admin', { replace: true });
    } catch (err) {
      if (err.status === 401) {
        redirectUnauthorized();
        return;
      }
      setErrors([err.message || 'Delete failed']);
      setBusy('');
    }
  };

  const handlePreview = async () => {
    setPreviewError('');
    setPreviewEvent(null);
    try {
      const payload = normalizeForSave(program);
      const outcome = await previewProgram(payload, previewOccId);
      setPreviewEvent(outcome.event);
      if (outcome.warnings) setWarnings(formatMessages(outcome.warnings));
    } catch (err) {
      if (err.status === 401) {
        redirectUnauthorized();
        return;
      }
      setPreviewError(err.message || 'Preview failed');
      if (err.errors) setErrors(formatMessages(err.errors));
    }
  };

  const venueOptions = useMemo(() => program?.venues || [], [program]);

  if (loading || !program) {
    return (
      <div className="admin-page">
        <p className="admin-muted">{loading ? 'Loading…' : 'Program unavailable.'}</p>
        {errors.length > 0 && (
          <ul className="admin-error-list">
            {errors.map((msg) => (
              <li key={msg}>{msg}</li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  return (
    <div className="admin-page admin-page--wide">
      <div className="admin-topbar">
        <div>
          <Link to="/admin" className="admin-back">
            ← All programs
          </Link>
          <h1 className="admin-title">{isNew ? 'New program' : program.title || id}</h1>
          <p className="admin-muted">
            Status:{' '}
            <span className={`admin-status admin-status--${status}`}>{status}</span>
            {program.updated_at && ` · Updated ${new Date(program.updated_at).toLocaleString()}`}
          </p>
        </div>
        <div className="admin-topbar__actions">
          <button type="button" className="admin-btn" onClick={handleDelete} disabled={!!busy}>
            {isNew ? 'Cancel' : 'Delete'}
          </button>
          <button
            type="button"
            className="admin-btn"
            onClick={handleSave}
            disabled={!!busy}
          >
            {busy === 'save' ? 'Saving…' : 'Save draft'}
          </button>
          <button
            type="button"
            className="admin-btn admin-btn--primary"
            onClick={handlePublish}
            disabled={!!busy || isNew}
            title={isNew ? 'Save first' : 'Validate and publish to public listings'}
          >
            {busy === 'publish' ? 'Publishing…' : 'Publish'}
          </button>
        </div>
      </div>

      {success && <p className="admin-success">{success}</p>}
      {errors.length > 0 && (
        <div className="admin-banner admin-banner--error">
          <strong>Errors</strong>
          <ul>
            {errors.map((msg) => (
              <li key={msg}>{msg}</li>
            ))}
          </ul>
        </div>
      )}
      {warnings.length > 0 && (
        <div className="admin-banner admin-banner--warn">
          <strong>Warnings</strong>
          <ul>
            {warnings.map((msg) => (
              <li key={msg}>{msg}</li>
            ))}
          </ul>
        </div>
      )}

      <section className="admin-section">
        <h2 className="admin-section__title">Shared program fields</h2>
        <div className="admin-grid">
          <label className="admin-label admin-label--required">
            Title *
            <input
              className="admin-input"
              value={program.title}
              onChange={(e) => updateField('title', e.target.value)}
            />
          </label>
          <label className="admin-label admin-label--required">
            Event URL *
            <input
              className="admin-input"
              type="url"
              placeholder="https://"
              value={program.event_url}
              onChange={(e) => updateField('event_url', e.target.value)}
            />
          </label>
          <label className="admin-chip admin-label--check admin-label--full">
            <input
              type="checkbox"
              checked={Boolean(program.use_event_url_as_ticket_url)}
              onChange={(e) => updateField('use_event_url_as_ticket_url', e.target.checked)}
            />
            Use event URL as ticket link when Ticket URL is blank
          </label>
          <p className="admin-muted admin-label--full">
            Unchecked (default): Get tickets only appears if a show has its own Ticket URL.
          </p>
          <label className="admin-label admin-label--required admin-label--full">
            Description *
            <textarea
              className="admin-textarea"
              rows={5}
              value={program.description}
              onChange={(e) => updateField('description', e.target.value)}
            />
          </label>
          <label className="admin-label">
            Default price
            <input
              className="admin-input"
              value={program.price || ''}
              onChange={(e) => updateField('price', e.target.value)}
              placeholder="$25–$100"
            />
          </label>
          <label className="admin-label">
            Audience suitability
            <input
              className="admin-input"
              value={program.audience_suitability || ''}
              onChange={(e) => updateField('audience_suitability', e.target.value)}
            />
          </label>
        </div>

        <fieldset className="admin-fieldset">
          <legend className="admin-label--required">Categories *</legend>
          <div className="admin-chips">
            {categories.map((cat) => (
              <label key={cat} className="admin-chip">
                <input
                  type="checkbox"
                  checked={(program.categories || []).includes(cat)}
                  onChange={() => toggleCategory(cat)}
                />
                {cat}
              </label>
            ))}
          </div>
        </fieldset>
      </section>

      <section className="admin-section">
        <h2 className="admin-section__title">Organizer (private — never published)</h2>
        <div className="admin-grid">
          <label className="admin-label">
            Name
            <input
              className="admin-input"
              value={program.organizer_name || ''}
              onChange={(e) => updateField('organizer_name', e.target.value)}
            />
          </label>
          <label className="admin-label">
            Email
            <input
              className="admin-input"
              type="email"
              value={program.organizer_email || ''}
              onChange={(e) => updateField('organizer_email', e.target.value)}
            />
          </label>
          <label className="admin-label">
            Phone
            <input
              className="admin-input"
              value={program.organizer_phone || ''}
              onChange={(e) => updateField('organizer_phone', e.target.value)}
            />
          </label>
          <label className="admin-label admin-label--full">
            Internal notes
            <textarea
              className="admin-textarea"
              rows={3}
              value={program.notes || ''}
              onChange={(e) => updateField('notes', e.target.value)}
            />
          </label>
        </div>
      </section>

      <section className="admin-section">
        <h2 className="admin-section__title">Image (optional)</h2>
        <div className="admin-grid">
          <label className="admin-label">
            Image URL
            <input
              className="admin-input"
              type="url"
              value={(program.image && program.image.url) || ''}
              onChange={(e) => updateImage('url', e.target.value)}
            />
          </label>
          <label className="admin-label">
            Alt text
            <input
              className="admin-input"
              value={(program.image && program.image.alt_text) || ''}
              onChange={(e) => updateImage('alt_text', e.target.value)}
            />
          </label>
          <label className="admin-label">
            Credit
            <input
              className="admin-input"
              value={(program.image && program.image.credit) || ''}
              onChange={(e) => updateImage('credit', e.target.value)}
            />
          </label>
          <label className="admin-chip admin-label--check">
            <input
              type="checkbox"
              checked={Boolean(program.image && program.image.usage_permission_confirmed)}
              onChange={(e) => updateImage('usage_permission_confirmed', e.target.checked)}
            />
            Usage permission confirmed
          </label>
        </div>
        {program.image && program.image.url && (
          <div className="admin-image-preview">
            {imageLoadError ? (
              <p className="admin-error">
                Image failed to load — use a publicly reachable http(s) URL (this host returned an
                error for the current link).
              </p>
            ) : (
              <img
                src={program.image.url}
                alt={program.image.alt_text || 'Program'}
                onError={() => setImageLoadError(true)}
              />
            )}
          </div>
        )}
      </section>

      <section className="admin-section">
        <div className="admin-section__header">
          <h2 className="admin-section__title">Venues</h2>
          <button type="button" className="admin-btn" onClick={addVenue}>
            Add venue
          </button>
        </div>
        {(program.venues || []).map((venue, index) => (
          <div key={venue.id} className="admin-subcard">
            <div className="admin-subcard__head">
              <strong>Venue {index + 1}</strong>
              <button
                type="button"
                className="admin-btn admin-btn--tiny"
                onClick={() => removeVenue(index)}
                disabled={(program.venues || []).length <= 1}
              >
                Remove
              </button>
            </div>
            <div className="admin-grid">
              <label className="admin-label admin-label--required">
                Name *
                <input
                  className="admin-input"
                  value={venue.name}
                  onChange={(e) => updateVenue(index, 'name', e.target.value)}
                />
              </label>
              <label className="admin-label admin-label--required">
                Street *
                <input
                  className="admin-input"
                  value={venue.street}
                  onChange={(e) => updateVenue(index, 'street', e.target.value)}
                />
              </label>
              <label className="admin-label admin-label--required">
                City *
                <input
                  className="admin-input"
                  value={venue.city}
                  onChange={(e) => updateVenue(index, 'city', e.target.value)}
                />
              </label>
              <label className="admin-label admin-label--required">
                State *
                <input
                  className="admin-input"
                  value={venue.state}
                  onChange={(e) => updateVenue(index, 'state', e.target.value)}
                />
              </label>
              <label className="admin-label admin-label--required">
                ZIP *
                <input
                  className="admin-input"
                  value={venue.zip}
                  onChange={(e) => updateVenue(index, 'zip', e.target.value)}
                />
              </label>
              <label className="admin-label admin-label--required">
                Country *
                <input
                  className="admin-input"
                  value={venue.country}
                  onChange={(e) => updateVenue(index, 'country', e.target.value)}
                />
              </label>
            </div>
          </div>
        ))}
      </section>

      <section className="admin-section">
        <div className="admin-section__header">
          <h2 className="admin-section__title">Occurrences</h2>
          <button type="button" className="admin-btn" onClick={addOccurrence}>
            Add show
          </button>
        </div>
        <div className="admin-table-wrap">
          <table className="admin-table admin-table--compact">
            <thead>
              <tr>
                <th>Date</th>
                <th>Start</th>
                <th>End</th>
                <th>Venue</th>
                <th>Ticket URL</th>
                <th>Price</th>
                <th>Public notes</th>
                <th>Internal notes</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(program.occurrences || []).map((occ, index) => (
                <tr key={occ.id} className={previewOccId === occ.id ? 'admin-row--selected' : ''}>
                  <td>
                    <input
                      className="admin-input admin-input--sm"
                      type="date"
                      value={occ.date || ''}
                      onChange={(e) => updateOccurrence(index, 'date', e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      className="admin-input admin-input--sm"
                      type="time"
                      value={occ.start_time || ''}
                      onChange={(e) => updateOccurrence(index, 'start_time', e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      className="admin-input admin-input--sm"
                      type="time"
                      value={occ.end_time || ''}
                      onChange={(e) => updateOccurrence(index, 'end_time', e.target.value)}
                    />
                  </td>
                  <td>
                    <select
                      className="admin-input admin-input--sm"
                      value={occ.venue_id || ''}
                      onChange={(e) => updateOccurrence(index, 'venue_id', e.target.value)}
                    >
                      <option value="">Select…</option>
                      {venueOptions.map((v) => (
                        <option key={v.id} value={v.id}>
                          {v.name || v.id}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <input
                      className="admin-input admin-input--sm"
                      type="url"
                      value={occ.ticket_url || ''}
                      onChange={(e) => updateOccurrence(index, 'ticket_url', e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      className="admin-input admin-input--sm"
                      value={occ.price || ''}
                      onChange={(e) => updateOccurrence(index, 'price', e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      className="admin-input admin-input--sm"
                      value={occ.public_notes || ''}
                      onChange={(e) => updateOccurrence(index, 'public_notes', e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      className="admin-input admin-input--sm"
                      value={occ.internal_notes || ''}
                      onChange={(e) => updateOccurrence(index, 'internal_notes', e.target.value)}
                    />
                  </td>
                  <td className="admin-row-actions">
                    <button
                      type="button"
                      className="admin-btn admin-btn--tiny"
                      onClick={() => setPreviewOccId(occ.id)}
                    >
                      Select
                    </button>
                    <button
                      type="button"
                      className="admin-btn admin-btn--tiny"
                      onClick={() => duplicateOccurrence(index)}
                    >
                      Dup
                    </button>
                    <button
                      type="button"
                      className="admin-btn admin-btn--tiny"
                      onClick={() => removeOccurrence(index)}
                      disabled={(program.occurrences || []).length <= 1}
                    >
                      Del
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="admin-section">
        <div className="admin-section__header">
          <h2 className="admin-section__title">Public card preview</h2>
          <button type="button" className="admin-btn" onClick={handlePreview}>
            Refresh preview
          </button>
        </div>
        <p className="admin-muted">
          Selected occurrence: <code>{previewOccId || 'none'}</code>. Private organizer fields and
          internal notes are never shown here.
        </p>
        {previewError && <p className="admin-error">{previewError}</p>}
        {previewEvent ? (
          <div className="admin-preview-card">
            {previewEvent.ImageURL && (
              <img
                className="admin-preview-card__image"
                src={previewEvent.ImageURL}
                alt={previewEvent.ImageAlt || previewEvent['Event Name'] || 'Preview'}
              />
            )}
            <h3>{previewEvent['Event Name']}</h3>
            <p>
              {previewEvent.Date} · {previewEvent.Time} · {previewEvent.Location}
            </p>
            {previewEvent.price && <p>Price: {previewEvent.price}</p>}
            {Array.isArray(previewEvent.categories) && previewEvent.categories.length > 0 && (
              <p>Categories: {previewEvent.categories.join(', ')}</p>
            )}
            <p className="admin-preview-card__desc">
              {(previewEvent.Summary || previewEvent.Description || '').slice(0, 280)}
              {(previewEvent.Summary || previewEvent.Description || '').length > 280 ? '…' : ''}
            </p>
            <p className="admin-muted">
              Ticket/details URL: {previewEvent.TicketURL || previewEvent.Details || '—'}
            </p>
            <p className="admin-muted">Event URL: {previewEvent.EventURL || '—'}</p>
            {previewEvent.ImageURL && (
              <p className="admin-muted">Image URL: {previewEvent.ImageURL}</p>
            )}
          </div>
        ) : (
          <p className="admin-muted">Click “Refresh preview” to see the adapted public listing.</p>
        )}
      </section>
    </div>
  );
}

export default AdminProgramEditor;
